import { get, merge } from "lodash";
import { Number, File, Text, Checkbox, Select, List, Radio, Range, Location, LocationMatrix } from "./Input";
import { N3dSurface, BarChart, PieChart, TimeChart, WaterfallChart, RawPlotlyChart } from "./Output";
import { Anchors, Explorer, Group, Tabs, Label } from "./Layout";
import Markdown from "components/Markdown";
import {
  I3dSurface,
  IBarChart,
  IBase,
  IChange,
  ICheckbox,
  IFile,
  IGroup,
  ITabs,
  ITab,
  ILabel,
  IModel,
  INumber,
  IPieChart,
  IResult,
  ISelect,
  ITemplate,
  IText,
  ITimeChart,
  IList,
  IName,
  IValue,
  IGetValue,
  IExplorer,
  IGetHidden,
  IAnchors,
  IRadio,
  IRange,
  IMetadata,
  IImage,
  IHandlers,
  IGetOverridden,
  IGetPath,
  IOverridden,
  IGetElement,
  IGetProperty,
  IWaterfallChart,
  IInput,
  IRawPlotlyChart,
  IListContext,
  ILocation,
  ILocationMatrix,
} from "./types";
import React, { ReactElement } from "react";
import { Tree, buildTree } from "utils/tree";
import Image from "./Layout/Image";
import { buildMetadata } from "./metadata";
import { buildPath } from "./common";
import { evaluate } from "./action";
import PrintableItem from "./PrintableItem";
/**
 * Builds an explorer model for the supplied layout.
 * @param layout
 * @returns
 */
export const buildExplorer = (layout: IExplorer): Tree<IBase> => {
  const isTree = layout.variant === "tree";
  const isControlled = (n: IName) => {
    if (layout.includes) {
      return layout.includes.includes(n);
    } else if (layout.excludes) {
      return !layout.excludes.includes(n);
    } else {
      return true;
    }
  };
  const pid = 1;
  let id = pid;
  const models: Array<IBase> = [];
  const build = (v: IBase, p?: number): void => {
    const item = merge(v, { id: id++, parentId: p });
    switch (v.type) {
      // these types can be nested
      case "explorer":
        models.push(item);
        (v as IExplorer).items.forEach((i) => build(i, isTree ? item.id : pid));
        break;
      case "group":
      case "anchors":
        if (isControlled(v.name)) {
          models.push(item);
        }
        (v as IGroup).items.forEach((i) => build(i, isTree ? item.id : pid));
        break;
      // these types are not nested
      case "tabs":
      case "list":
      case "3d_surface":
      case "bar_chart":
      case "waterfall_chart":
      case "pie_chart":
      case "time_chart":
        if (isControlled(v.name)) {
          models.push(item);
        }
        break;
      // these types can't be hidden individually
      case "number":
      case "text":
      case "select":
      case "checkbox":
      case "file":
      case "radio":
      case "range":
      case "location":
      case "location_matrix":
      case "label":
      case "image":
      case "calculate":
      default:
        break;
    }
  };
  build(layout);
  return buildTree(models);
};

export const getTemplateMutators = (
  model: IModel,
  metadata: IMetadata,
  handlers: IHandlers = {},
  storedModel?: IModel
) => {
  const {
    handleLoading,
    handleFault,
    handleHidden,
    handleProperty,
    handleAssistantContextTag,
    assistantContextModelName,
  } = handlers;

  const getStoredValue = storedModel ? ((p: IName) => get(storedModel, p)) : undefined;

  return {
    getValue: ((p) => get(model, p)) as IGetValue,
    getStoredValue,
    setStoredValue: (storedModel && handlers.handleStoredChange)
      ? ((p: IName, value: IValue) => handlers.handleStoredChange?.(p, value))
      : undefined,
    getPath: ((p) => p) as IGetPath,
    getElement: ((p) => metadata.inputs?.findNode(p)) as IGetElement,
    isHidden: ((p) => metadata.hidden.includes(p)) as IGetHidden,
    isOverridden: ((p) => metadata.overridden.includes(p)) as IGetOverridden,
    getProperty: ((p) => metadata.properties[p]) as IGetProperty,
    handleLoading,
    handleFault,
    handleHidden,
    handleProperty,
    handleAssistantContextTag,
    assistantContextModelName,
  };
};

export const getInputMutators = (
  baseMutators: ReturnType<typeof getTemplateMutators>,
  listContext?: IListContext,
  handleChange?: IChange,
  handleOverridden?: IOverridden
) => {
  if (!listContext) return { ...baseMutators, handleChange, handleOverridden };

  const { name, index } = listContext;

  return {
    ...baseMutators,
    getValue: ((p: IName) => baseMutators.getValue(buildPath(name, index, p))) as IGetValue,
    getPath: ((p: IName) => buildPath(name, index, p)) as IGetPath,
    getElement: ((p: IName) => baseMutators.getElement(buildPath(name, index, p))) as IGetElement,
    isOverridden: ((p) => baseMutators.isOverridden(buildPath(name, index, p))) as IGetOverridden,
    handleOverridden: ((p, v) => handleOverridden?.(buildPath(name, index, p), v)) as IOverridden,
    handleChange: handleChange
      ? ((p: IName, w: IValue) => handleChange(buildPath(name, index, p), w)) as IChange
      : undefined,
  };
};

/**
 * Renders the input form for the supplied template.
 * @param template
 * @param model
 * @param metadata
 * @param handlers
 * @returns
 */
export const renderInputItems = (
  items: Array<IBase>,
  model: IModel,
  metadata: IMetadata = buildMetadata(),
  handlers: IHandlers = {},
  storedModel?: IModel,
  options?: { hideAnchors?: boolean; wrapForm?: boolean; aiChangedInputPaths?: Array<string> }
): React.ReactNode => {
  const { handleCreate, handleRemove, handleChange, handleOverridden, ...otherHandlers } = handlers;
  const { hideAnchors, wrapForm = false, aiChangedInputPaths } = options || {};

  const baseMutators = getTemplateMutators(model, metadata, otherHandlers, storedModel);

  const render = (v: IBase, listContext?: IListContext): React.ReactNode => {
    const mutators = getInputMutators(baseMutators, listContext, handleChange, handleOverridden);

    switch (v.type) {
      case "list":
        return (
          <List
            key={v.name}
            template={v as IList}
            model={model}
            metadata={metadata}
            mutators={{ ...mutators, handleCreate, handleRemove }}
          >
            {!(v.hidden && evaluate(model, v.hidden, mutators.getValue)) &&
              (get(model, (v as IList).name) as Array<any>).map((_a, n) =>
                (v as IList).items.map((i) => render(i, { name: (v as IList).name, index: n }))
              )}
          </List>
        );
      case "group":
        return (
          <Group
            key={listContext ? `${listContext.name}_${listContext.index}_${v.name}` : v.name}
            template={v as IGroup}
            model={model}
            metadata={metadata}
            mutators={mutators}
          >
            {!(v.hidden && evaluate(model, v.hidden, mutators.getValue)) && (v as IGroup).items.map((i) => render(i, listContext))}
          </Group>
        );
      case "anchors":
        if (hideAnchors) {
          return (
            <React.Fragment key={v.name}>
              {!(v.hidden && evaluate(model, v.hidden, mutators.getValue)) &&
                (v as IAnchors).items.map((i) => render(i, listContext))}
            </React.Fragment>
          );
        }
        return (
          <Anchors
            key={v.name}
            template={v as IAnchors}
            model={model}
            metadata={metadata}
            mutators={mutators}
            aiChangedInputPaths={aiChangedInputPaths}
          >
            {!(v.hidden && evaluate(model, v.hidden, mutators.getValue)) && (v as IAnchors).items.map((i) => render(i, listContext))}
          </Anchors>
        );
      case "tabs":
        return (
          <Tabs
            key={v.name}
            id={v.name}
            template={v as ITabs}
            model={model}
            metadata={metadata}
            mutators={mutators}
            tabs={(v as ITabs).items.map((i) => ({ tab: i, component: render(i, listContext) as ReactElement<any, any> }))}
          />
        );
      case "tab":
        return <div>{!(v.hidden && evaluate(model, v.hidden, mutators.getValue)) && (v as ITab).items.map((i) => render(i, listContext))}</div>;
      case "explorer":
        const tree = buildExplorer(v as IExplorer);
        return (
          <Explorer
            key={v.name}
            template={v as IExplorer}
            model={model}
            metadata={metadata}
            tree={tree}
            mutators={mutators}
          >
            {!(v.hidden && evaluate(model, v.hidden, mutators.getValue)) && (v as IExplorer).items.map((i) => render(i, listContext))}
          </Explorer>
        );
      case "label":
        return (
          <Label
            key={v.name}
            template={v as ILabel}
            model={model}
            metadata={metadata}
            mutators={mutators}
          />
        );
      case "image":
        return (
          <Image
            key={v.name}
            template={v as IImage}
            model={model}
            metadata={metadata}
            mutators={mutators}
          />
        );
      case "markdown":
        return <Markdown key={v.name} markdown={v.label} />;
      case "number":
        return (
          <Number
            key={v.name}
            template={v as INumber}
            model={model}
            metadata={metadata}
            mutators={mutators}
          />
        );
      case "checkbox":
        return (
          <Checkbox
            key={v.name}
            template={v as ICheckbox}
            model={model}
            metadata={metadata}
            mutators={mutators}
          />
        );
      case "text":
        return (
          <Text
            key={v.name}
            template={v as IText}
            model={model}
            metadata={metadata}
            mutators={mutators}
          />
        );
      case "file":
        return (
          <File
            key={v.name}
            template={v as IFile}
            model={model}
            metadata={metadata}
            mutators={mutators}
          />
        );
      case "select":
        return (
          <Select
            key={v.name}
            template={v as ISelect}
            model={model}
            metadata={metadata}
            mutators={mutators}
          />
        );
      case "radio":
        return (
          <Radio
            key={v.name}
            template={v as IRadio}
            model={model}
            metadata={metadata}
            mutators={mutators}
            listContext={listContext}
          />
        );
      case "range":
        return (
          <Range
            key={v.name}
            template={v as IRange}
            model={model}
            metadata={metadata}
            mutators={mutators}
          />
        );
      case "location":
        return (
          <Location
            key={v.name}
            template={v as ILocation}
            model={model}
            metadata={metadata}
            mutators={mutators}
          />
        );
      case "location_matrix":
        return (
          <LocationMatrix
            key={v.name}
            template={v as ILocationMatrix}
            model={model}
            metadata={metadata}
            mutators={mutators}
          />
        );
      case "calculate":
        return null;
      case "bar_chart":
        return (
          <BarChart
            key={v.name}
            template={v as IBarChart}
            model={model}
            result={model}
            metadata={metadata}
            mutators={mutators}
          />
        );
      case "waterfall_chart":
        return (
          <WaterfallChart
            key={v.name}
            template={v as IWaterfallChart}
            model={model}
            result={model}
            metadata={metadata}
            mutators={mutators}
          />
        );
      case "pie_chart":
        return (
          <PieChart
            key={v.name}
            template={v as IPieChart}
            model={model}
            result={model}
            metadata={metadata}
            mutators={mutators}
          />
        );
      case "raw_plotly_chart":
        return (
          <RawPlotlyChart
            key={v.name}
            template={v as IRawPlotlyChart}
            model={model}
            result={model}
            metadata={metadata}
            mutators={mutators}
          />
        );
      case "time_chart":
        return (
          <TimeChart
            key={v.name}
            template={v as ITimeChart}
            model={model}
            result={model}
            metadata={metadata}
            mutators={mutators}
          />
        );
      case "3d_surface":
        return (
          <N3dSurface
            key={v.name}
            template={v as I3dSurface}
            model={model}
            result={model}
            metadata={metadata}
            mutators={mutators}
          />
        );
      default:
        throw new Error(`${v.type} cannot exist as an input element.`);
    }
  };
  const content = items?.map((i) => render(i));
  if (wrapForm) {
    return <form>{content}</form>;
  }
  return content;
};

/**
 * Renders the input form for the supplied template.
 * @param template
 * @param model
 * @param metadata
 * @param handlers
 * @returns
 */
export const renderInput = (
  template: ITemplate,
  model: IModel,
  metadata: IMetadata = buildMetadata(),
  handlers: IHandlers = {},
  storedModel?: IModel,
  options?: { hideAnchors?: boolean; aiChangedInputPaths?: Array<string> }
): React.ReactNode => {
  return renderInputItems(
    template.inputs,
    model,
    metadata,
    handlers,
    storedModel,
    { ...options, wrapForm: true }
  );
};

/**
 * Renders the output result for the supplied template.
 * @param template
 * @param model
 * @param result
 * @param metadata
 * @param handlers
 * @returns
 */
export const renderOutput = (
  template: ITemplate,
  model: IModel,
  result: IResult,
  metadata: IMetadata = buildMetadata(),
  handlers: IHandlers = {}
): React.ReactNode => {
  const { handleHidden, handleLoading, handleFault, handleProperty, handleChange } = handlers;
  const getValue = ((p) => get(model, p) || get(result, p)) as IGetValue;
  const getPath = ((p) => p) as IGetPath;
  const getElement = ((p) => metadata.outputs?.findNode(p)) as IGetElement;
  const isHidden = ((p) => metadata.hidden.includes(p)) as IGetHidden;
  const isOverridden = ((p) => metadata.overridden.includes(p)) as IGetOverridden;
  const getProperty = ((p) => metadata.properties[p]) as IGetProperty;
  const render = (v: IBase): React.ReactNode => {
    switch (v.type) {
      case "group":
        return (
          <Group
            key={v.name}
            template={v as IGroup}
            model={model}
            result={result}
            metadata={metadata}
            mutators={{
              getValue,
              getPath,
              getElement,
              isHidden,
              isOverridden,
              handleLoading,
              handleFault,
              getProperty,
              handleProperty,
            }}
          >
            {!(v.hidden && evaluate(model, v.hidden, undefined, result)) && (v as IGroup).items.map((i) => render(i))}
          </Group>
        );
      case "anchors":
        return (
          <Anchors
            key={v.name}
            template={v as IAnchors}
            model={model}
            metadata={metadata}
            result={result}
            mutators={{
              getValue,
              getPath,
              getElement,
              isHidden,
              isOverridden,
              handleLoading,
              handleFault,
              getProperty,
              handleProperty,
            }}
          >
            {!(v.hidden && evaluate(model, v.hidden, undefined, result)) && (v as IAnchors).items.map((i) => render(i))}
          </Anchors>
        );
      case "tabs":
        return (
          <Tabs
            key={v.name}
            id={v.name}
            template={v as ITabs}
            model={model}
            metadata={metadata}
            mutators={{
              getValue,
              getPath,
              getElement,
              isHidden,
              isOverridden,
              handleLoading,
              handleFault,
              getProperty,
              handleProperty,
            }}
            tabs={(v as ITabs).items.map((i) => ({ tab: i, component: render(i) as ReactElement<any, any> }))}
          />
        );
      case "tab":
        return (
          <div>
            {!(v.hidden && evaluate(model, v.hidden, undefined, result)) && (v as ITab).items.map((i) => render(i))}
          </div>
        );
      case "explorer":
        const tree = buildExplorer(v as IExplorer);
        return (
          <Explorer
            key={v.name}
            template={v as IExplorer}
            model={model}
            result={result}
            metadata={metadata}
            tree={tree}
            mutators={{
              getValue,
              getPath,
              getElement,
              isHidden,
              isOverridden,
              handleHidden,
              handleLoading,
              handleFault,
              getProperty,
              handleProperty,
            }}
          >
            {!(v.hidden && evaluate(model, v.hidden, undefined, result)) &&
              (v as IExplorer).items.map((i) => render(i))}
          </Explorer>
        );
      case "label":
        return (
          <Label
            key={v.name}
            template={v as ILabel}
            model={model}
            result={result}
            metadata={metadata}
            mutators={{
              getValue,
              getPath,
              getElement,
              isHidden,
              isOverridden,
              handleLoading,
              handleFault,
              getProperty,
              handleProperty,
            }}
          />
        );
      case "image":
        return (
          <Image
            key={v.name}
            template={v as IImage}
            model={model}
            result={result}
            metadata={metadata}
            mutators={{
              getValue,
              getPath,
              getElement,
              isHidden,
              isOverridden,
              handleLoading,
              handleFault,
              getProperty,
              handleProperty,
            }}
          />
        );
      case "bar_chart":
        return (
          <BarChart
            key={v.name}
            template={v as IBarChart}
            model={model}
            result={result}
            metadata={metadata}
            mutators={{
              getValue,
              getPath,
              getElement,
              isHidden,
              isOverridden,
              handleLoading,
              handleFault,
              getProperty,
              handleProperty,
            }}
          />
        );
      case "raw_plotly_chart":
        return (
          <RawPlotlyChart
            key={v.name}
            template={v as IRawPlotlyChart}
            model={model}
            result={result}
            metadata={metadata}
            mutators={{
              getValue,
              getPath,
              getElement,
              isHidden,
              isOverridden,
              handleLoading,
              handleFault,
              getProperty,
              handleProperty,
            }}
          />
        );
      case "waterfall_chart":
        return (
          <WaterfallChart
            key={v.name}
            template={v as IWaterfallChart}
            model={model}
            result={result}
            metadata={metadata}
            mutators={{
              getValue,
              getPath,
              getElement,
              isHidden,
              isOverridden,
              handleLoading,
              handleFault,
              getProperty,
              handleProperty,
            }}
          />
        );
      case "pie_chart":
        return (
          <PieChart
            key={v.name}
            template={v as IPieChart}
            model={model}
            result={result}
            metadata={metadata}
            mutators={{
              getValue,
              getPath,
              getElement,
              isHidden,
              isOverridden,
              handleLoading,
              handleFault,
              getProperty,
              handleProperty,
            }}
          />
        );
      case "time_chart":
        return (
          <TimeChart
            key={v.name}
            template={v as ITimeChart}
            model={model}
            result={result}
            metadata={metadata}
            mutators={{
              getValue,
              getPath,
              getElement,
              isHidden,
              isOverridden,
              handleLoading,
              handleFault,
              getProperty,
              handleProperty,
            }}
          />
        );
      case "3d_surface":
        return (
          <N3dSurface
            key={v.name}
            template={v as I3dSurface}
            model={model}
            result={result}
            metadata={metadata}
            mutators={{
              getValue,
              getPath,
              getElement,
              isHidden,
              isOverridden,
              handleLoading,
              handleFault,
              getProperty,
              handleProperty,
            }}
          />
        );
      case "select":
        return (
          <Select
            key={v.name}
            template={v as ISelect}
            model={model}
            result={result}
            metadata={metadata}
            mutators={{
              getValue,
              getPath,
              getElement,
              isHidden,
              isOverridden,
              handleLoading,
              handleFault,
              handleChange,
            }}
          />
        );
      case "radio":
        return (
          <Radio
            key={v.name}
            template={v as IRadio}
            model={model}
            metadata={metadata}
            mutators={{
              getValue,
              getPath,
              getElement,
              isHidden,
              isOverridden,
              handleLoading,
              handleFault,
              handleChange,
            }}
          />
        )
      case "checkbox":
        return (
          <Checkbox
            key={v.name}
            template={v as ICheckbox}
            model={model}
            metadata={metadata}
            mutators={{
              getValue,
              getPath,
              getElement,
              isHidden,
              isOverridden,
              handleLoading,
              handleFault,
              handleChange,
            }}
          />
        )
      case "markdown":
        return <Markdown key={v.name} markdown={v.label} />;
      case "calculate":
        return null;
      default:
        throw new Error(`${v.type} cannot exist as an output element.`);
    }
  };
  return <form>{template.outputs?.map((i) => render(i))}</form>;
};

/**
 * Renders the printable input
 * @param template
 * @param model
 * @param metadata
 * @returns
*/
export const renderPrintable = (template: ITemplate, model: IModel, metadata: IMetadata, hideAnchors: boolean = false) => {
  const hiddenList: Array<IName> = metadata.hiddenInput;

  const baseMutators = getTemplateMutators(model, {
    ...metadata,
    hidden: hiddenList,
  });

  const render = (v: IBase, listContext?: { name: IName; index: number; itemLabel?: string }): React.ReactNode => {
    const mutators = getInputMutators(baseMutators, listContext);

    if (v.hidden && evaluate(model, v.hidden, mutators.getValue)) {
      return null;
    }

    switch (v.type) {
      case "group":
        return (
          <PrintableItem key={v.name} template={v as IGroup} hiddenList={hiddenList} getValue={mutators.getValue} model={model} listContext={listContext}>
            {(v as IGroup).items.map((i) => render(i, listContext))}
          </PrintableItem>
        );
      case "anchors":
        return !hideAnchors ? (
          <Anchors
            key={v.name}
            template={v as IAnchors}
            model={model}
            metadata={metadata}
            mutators={mutators}
            reduceHeight
          >
            {(v as IAnchors).items.map((i) => render(i, listContext))}
          </Anchors>
        ) : (
          <PrintableItem key={v.name} template={v as IAnchors} hiddenList={hiddenList} getValue={mutators.getValue} model={model} listContext={listContext}>
            {(v as IAnchors).items.map((i) => render(i, listContext))}
          </PrintableItem>
        );
      case "tabs":
        return (
          <PrintableItem key={v.name} template={v as ITabs} hiddenList={hiddenList} getValue={mutators.getValue} model={model} listContext={listContext}>
            {(v as ITabs).items.map((i) => render(i, listContext))}
          </PrintableItem>
        );
      case "tab":
        return (
          <PrintableItem key={v.name} template={v as ITab} hiddenList={hiddenList} getValue={mutators.getValue} model={model} listContext={listContext}>
            {(v as ITab).items.map((i) => render(i, listContext))}
          </PrintableItem>
        );
      case "list":
        return (
          <PrintableItem key={v.name} template={v as IList} hiddenList={hiddenList} getValue={mutators.getValue} model={model} listContext={listContext}>
            {(get(model, (v as IList).name) as Array<any>).map((_a, n) => (
              <PrintableItem
                key={n}
                template={{
                  type: "group",
                  name: `${v.name}_${n}`,
                  label: `${(v as IList).item_label} ${n + 1}`
                } as IGroup}
                hiddenList={hiddenList}
                getValue={mutators.getValue}
                model={model}
              >
                {(v as IList).items.map((i) => render(i, { name: (v as IList).name, index: n }))}
              </PrintableItem>
            ))}
          </PrintableItem>
        );
      case "number":
      case "text":
      case "select":
      case "checkbox":
      case "file":
      case "radio":
      case "range":
      case "label":
        return <PrintableItem key={v.name} template={v as IInput} hiddenList={hiddenList} getValue={mutators.getValue} model={model} listContext={listContext} />;
    }
  };

  return <div>{template.inputs?.map((i) => render(i))}</div>;
};
